import './SettingsSyncToCal.scss'
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AcDialog from '../AcDialog/AcDialog';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SyncToCalDialog from '../SyncToCalDialog/SyncToCalDialog';
import { checkAccountEnabled, findActiveAccounts, getBackgroundColorForStatus, getCalendarName, getEventsContent, getSharingType, calendarFilter, isNoAccountLinked, getNotificationType, getErrorBorderClasses, isError, getLastSyncOn, IPADVIEWCONSTANT, notificationCodes, checkDataFromAccountsApiResponse } from './SettingsSyncToCalHelper';
import SyncAddAccountDialog from '../SyncAddAccountDialog/SyncAddAccountDialog';
import { shareEventsColumns, getNickNamesFromAccounts, getAvailableEmailIdsFromAccounts } from '../SyncAddAccountDialog/SyncToCalendarHelper';
import { useSettingsPageProvider } from '../../providers/SettingsProvider';
import { capitalizeFirstLetter } from '../../helpers/commonHelpers';
import Loader from '../Loader/Loader';
import SettingsNotification from './SettingsNotification';
import { useSyncToCalendarProvider } from '../../providers/SyncToCalendarProvider';
import { SettingsSync } from '../../models/SyncToCalendar';
import SettingsNoData from './SettingsNoData';
import { Link } from "react-router-dom";
import { Button } from '@mui/material';

export default function SettingsSyncToCal() {
    const { t } = useTranslation();
    const settingsContext = useSettingsPageProvider();
    const [syncToOpen, setSyncToOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [syncAddAccountOpen, setSyncAddAccountOpen] = useState(false);
    const [shareEventsCol, setShareEventsCol] = useState<any>({});
    const [successMsgTimeOut, setSuccessMsgTimeOut] = useState(false);
    const [mySyncedData, setMySyncedData] = useState<SettingsSync[]>([]);
    const [isIpadView, setIsIpadView] = useState(false);
    const [deleteNickName, setDeleteNickName] = useState('');
    const [syncDelete, setSyncDelete] = useState({ operation: '', emailId: '', accountType: '' });
    const [dataFromEditPopUp, setDataFromEditPopUp] = useState<any>({});
    const [successMsgDetails, setSuccessMsgDetails] = useState({ emailId: '', nickName: '' });
    const [operation, setOperation] = useState({ add: false, edit: false });
    const syncToCalendarContext = useSyncToCalendarProvider();
    const [accountNickNames, setAccountNickNames] = useState([]);
    const [availableEmailIds, setAvailableEmailIds] = useState({});
    const [hideEditDialog, setHideEditDialog] = useState(false);
    const [errorDialog, setErrorDialog] = useState(false);

    window.addEventListener('resize', windowResize);

    function windowResize() {
        const { innerWidth } = window;
        setIsIpadView(innerWidth <= IPADVIEWCONSTANT.MAXWIDTH && innerWidth > IPADVIEWCONSTANT.MINWIDTH);
    }

    useEffect(() => {
        if (!settingsContext?.syncedCalendarData?.length) {
            setMySyncedData([]);
        }
        else {
            if (!checkDataFromAccountsApiResponse(settingsContext?.syncedCalendarData)) {
                const orderedCalData = calendarFilter(settingsContext?.syncedCalendarData);
                setMySyncedData(orderedCalData);
                setAvailableEmailIds(getAvailableEmailIdsFromAccounts(orderedCalData));
                setAccountNickNames(getNickNamesFromAccounts(orderedCalData));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsContext?.syncedCalendarData]);

    const handleClose = () => {
        setSyncToOpen(false);
        setDeleteOpen(false);
        setSyncAddAccountOpen(false);
        setErrorDialog(false);
    }

    const onClickAdd = () => {
        setSuccessMsgTimeOut(false);
        setErrorDialog(false);
        setSyncToOpen(true);
        setOperation({ add: true, edit: false });
    }

    // Invalid token or timeout error Dialog content
    const errorDialogData = {
        title: t('myBlockMonth.syncToCalendar.addAccErrorDialogHeading'),
        description: t('myBlockMonth.syncToCalendar.addAccErrorDialogContent'),
        actionLabel: t('myBlockMonth.syncToCalendar.addAccountCloseCancel'),
        handleAction: handleClose,
        secondaryActionLabel: t('myBlockMonth.syncToCalendar.addAccTryAgain'),
        handleSecondaryAction:  onClickAdd 
    };

    const handleTokenError = () => {
        setErrorDialog(true);
        setSyncToOpen(false);
    }

    const syncToData = {
        titleMain: t('myBlockMonth.syncToCalendar.syncToCal'),
        titleSub: t('myBlockMonth.syncToCalendar.chooseCalendarType'),
        handleAction: handleClose,
        settingsScreen: true,
        setSuccessMsgDetails: setSuccessMsgDetails,
        setSuccessMsgTimeOut: setSuccessMsgTimeOut,
        accountNickNames,
        availableEmailIds,
        handleTokenError: handleTokenError,
    }

    //To delete the synced Calendar
    const handleDeleteSyncedCal = (data, editPopUp) => {
        setDeleteNickName(capitalizeFirstLetter(data?.[data.emailId].accountNickName));
        setSyncDelete({
            operation: 'delete',
            emailId: data?.emailId,
            accountType: data?.accountType
        })
        setDeleteOpen(true);
    }

    //To delete the synced Calendar From the edit Popup
    const handleDeleteFromEditPopUp = () => {
        if (!dataFromEditPopUp) return;
        else handleDeleteSyncedCal(dataFromEditPopUp, true);
        setSyncAddAccountOpen(false);
    }

    const deleteSyncedCalendar = async () => {
        if (!syncDelete) return;
        await settingsContext.deleteSyncedCalendarData(syncDelete);
        settingsContext.getSyncedCalendarData();
        handleClose();
    }

    const removeData = {
        title: `Are you sure you want to delete the “${deleteNickName}” account?`,
        description: t('settingsScreen.deleteText'),
        actionLabel: t('settingsScreen.deleteCancel'),
        handleAction: handleClose,
        secondaryActionLabel: t('settingsScreen.delete'),
        handleSecondaryAction: deleteSyncedCalendar,
    }

    const generateCheckBoxEvents = (data) => {
        const result = {};
        const events = data?.[data.emailId]?.events;

        if (events) {
            Object.keys(events).forEach(event => {
                result[event] = events[event].selected;
            })
            var eventKeyNames = Object.keys(events);
            if (eventKeyNames.includes("all") && result["all"]) { setShareEventsCol({ ...shareEventsColumns }); }
            else { setShareEventsCol({ ...result }); }
        }
    }

    const handleSuccessClose = () => {
        setSyncAddAccountOpen(false);
    }

    //To edit the synced Calendar
    const handleEditSyncedCal = (data) => {
        if (!data) return;
        setHideEditDialog(false);
        setSuccessMsgTimeOut(false);
        generateCheckBoxEvents(data);
        setDataFromEditPopUp({ ...data });
        setSyncAddAccountOpen(true);
        setOperation({ add: false, edit: true });
    }

    const syncAddAccountData = {
        actionLabel: t('myBlockMonth.syncToCalendar.syncCancel'),
        handleAction: handleClose,
        secondaryActionLabel: t('myBlockMonth.syncToCalendar.save'),
        shareEventsCol: shareEventsCol,
        setShareEventsCol: setShareEventsCol,
        operation: 'edit',
        thirdActionLabel: t('myBlockMonth.syncToCalendar.delete'),
        handleThirdAction: handleDeleteFromEditPopUp,
        dataFromEditPopUp: dataFromEditPopUp,
        handleSuccessClose: handleSuccessClose,
        setSuccessMsgDetails: setSuccessMsgDetails,
        setSuccessMsgTimeOut: setSuccessMsgTimeOut,
        isSettingsScreen: true,
        accountNickNames,
        availableEmailIds
    };

    const handleClearNotification = async (index) => {
        const accountData = { ...settingsContext?.syncedCalendarData[index] };
        if (accountData?.errorCode === notificationCodes.PAUSEDACCOUNTREMOVED) {
            await settingsContext.clearNotification(accountData?.emailId, accountData?.accountType);
            let onClearNotification: any = {};
            onClearNotification = {
                operation: 'delete',
                emailId: accountData?.emailId,
                accountType: accountData?.accountType
            }
            await settingsContext.deleteSyncedCalendarData(onClearNotification);
        }
        else await settingsContext.clearNotification(accountData?.emailId, accountData?.accountType);
        await settingsContext.getSyncedCalendarData();
    }

    const warningErrorContainer = {};

    return (
        settingsContext.syncToCalLoading || syncToCalendarContext.manualSyncLoading ? <Loader /> : (
            <>
                {checkDataFromAccountsApiResponse(settingsContext?.syncedCalendarData) ?
                    <SettingsNoData/> :
                    <div className='settings-sync-cal__cntr'>
                        <SyncToCalDialog open={syncToOpen} onClose={handleClose} data={syncToData} />
                        <AcDialog open={deleteOpen} onClose={handleClose} data={removeData} />
                        {syncAddAccountOpen && <SyncAddAccountDialog open={syncAddAccountOpen} onClose={handleClose} hideEditDialog={hideEditDialog} setHideEditDialog={setHideEditDialog} data={syncAddAccountData} />}
                        {errorDialog && <AcDialog open={errorDialog} onClose={handleClose} data={errorDialogData} />}

                        <div className='settings-sync-cal__header'>
                            <div className='settings-sync-cal__title-cntr'>
                                <p className='settings-sync-cal__header-title'>{t('settingsScreen.header')}</p>
                                {!isNoAccountLinked(mySyncedData) ? <p className='settings-sync-cal__header-how-to-sync'><Link to="/how-to-sync-cal" target='_blank' className="settings-sync-cal__header-how-to-sync-link">{t('settingsScreen.howToSync')}</Link></p> : ''}
                            </div>
                            {!isNoAccountLinked(mySyncedData) ?
                                <div className='settings-sync-cal__btn-cntr'>
                                    <p className={findActiveAccounts(mySyncedData) === 6 ? 'settings-sync-cal__header-account-limit-reached' : 'settings-sync-cal__header-account-available'}>
                                        {findActiveAccounts(mySyncedData) === 6 ? t('settingsScreen.accountMaxReached') : `${6 - findActiveAccounts(mySyncedData)} ${t('settingsScreen.accountsAvailable')}`}
                                    </p>
                                    <Button className='settings-sync-cal__header-add-account-btn' disabled={findActiveAccounts(mySyncedData) === 6} onClick={() => onClickAdd()}>
                                        <AddCircleOutlineIcon className="add-category-icon link-states" />&nbsp;&nbsp;<span className='settings-sync-cal__header-add-account-text'>{t('settingsScreen.addAccount')}</span>
                                    </Button>
                                </div> : ''
                            }
                        </div>

                        {successMsgTimeOut ?
                            <div className='settings-sync-cal__success-notification settings-sync-cal__notification'>
                                <i className='icon-Confirmation-circle-green' />
                                {operation.add && <p>{t('settingsScreen.addAccountSuccess',{email:successMsgDetails.emailId})} ({successMsgDetails.nickName}).
                                <span style={{display:"block"}}>{t('settingsScreen.addAccountSuccess1')}</span>
                                </p>}                      
                                
                                {operation.edit && <p>{t('settingsScreen.editAccountSuccess')} {successMsgDetails.nickName} {t('settingsScreen.editAccountSuccess1')}
                                <span style={{display:"block"}}>{t('settingsScreen.editAccountSuccess2')}</span>
                                </p>}
                            </div> : ''
                        }

                        {settingsContext?.syncedCalendarData?.map((data, index) => {
                            const type = getNotificationType(data?.errorCode);
                            const errorCode = parseInt(data?.errorCode);
                            //* FIX OD-6809 ODP-344 to show consolidated view of errors or display individual account errors *//
                            if(!(errorCode in warningErrorContainer) || errorCode === notificationCodes.ACCOUNTPAUSED ||  errorCode === notificationCodes.ACCOUNTPAUSED){
                                warningErrorContainer[errorCode] = errorCode;
                                return (type === 'error' || type === 'warning') &&
                                    <SettingsNotification type={type} errorCode={errorCode} message={data?.errorMessage} onClearNotification={() => handleClearNotification(index)} key={`settings-account-${index}`} />
                            }else{
                                return '';
                            }                            
                        })}

                        {isNoAccountLinked(mySyncedData) ?
                            <div className='settings-sync-cal__no-account-cntr'>
                                <p className='settings-sync-cal__no-account-start-syncing'>{t('settingsScreen.startSyncing')}</p>
                                <p className='settings-sync-cal__no-account-icon' onClick={() => onClickAdd()}><i className="icon-Sync-to-Calendar"></i></p>
                                <button className='settings-sync-cal__add-account-btn'
                                    onClick={() => onClickAdd()}
                                >
                                    <AddCircleOutlineIcon className="add-category-icon link-states" />&nbsp;&nbsp;{t('settingsScreen.addAccount')}
                                </button>
                                <div className='settings-sync-cal__no-account-text'>
                                    <><p className='settings-sync-cal__no-account-text1'>{t('settingsScreen.startSyncingtext1')}</p><b>{"up to 6 accounts."}</b></>
                                    <p className='settings-sync-cal__no-account-text2'>{t('settingsScreen.startSyncingtext2')}</p>
                                </div>
                                <p className='settings-sync-cal__no-account-faq'><Link to="/how-to-sync-cal" target='_blank' className="settings-sync-cal__no-account-faq-link">{t('settingsScreen.syncFaq')}</Link></p>
                            </div> : ''}

                        {mySyncedData.map((data, index) => (
                            <div className={'settings-sync-cal__account-cntr' + (getErrorBorderClasses(data?.errorCode))} key={`accounts-${index}`}>
                                <div className='settings-sync-cal__section-one'>
                                    <p className='settings-sync-cal__usage'>{getSharingType(data?.[data.emailId].usage)}</p>
                                    <p className='settings-sync-cal__nickname'>{capitalizeFirstLetter(data?.[data.emailId].accountNickName)}</p>
                                    <div className='settings-sync-cal__sync-on'><p >{`${t('settingsScreen.lastSyncOn')} ${getLastSyncOn(data?.lastSyncOn)}`}</p></div>
                                </div>
                                <div className='settings-sync-cal__section-two'>
                                    <p className='settings-sync-cal__calendar-type'>{t(getCalendarName(data?.accountType))}</p>
                                    <div className='settings-sync-cal__email-cntr'>
                                        <p className='settings-sync-cal__email-label'>{isIpadView ? t('settingsScreen.email') : t('settingsScreen.emailAddress')}</p>
                                        <p className='settings-sync-cal__email-content'>{data?.emailId}</p>
                                    </div>
                                    <div className='settings-sync-cal__events-cntr'>
                                        <p className='settings-sync-cal__events-label'>{isIpadView ? t('settingsScreen.events') : t('settingsScreen.eventsShared')}</p>
                                        <p className='settings-sync-cal__events-content'>{getEventsContent(data?.[data.emailId]?.events, data?.[data.emailId]?.enabled)}</p>
                                    </div>
                                </div>
                                <div className='settings-sync-cal__section-three'>
                                    <div className='settings-sync-cal__badge-cntr'>
                                        {data?.[data.emailId]?.enabled && <p className='settings-sync-cal__badge-of'>{`${index + 1} ${t('settingsScreen.of')}`}</p>}
                                        <p className='settings-sync-cal__badge-status' style={{ backgroundColor: `${getBackgroundColorForStatus(data?.[data.emailId].enabled, isError(data?.errorCode))}` }}>{t(checkAccountEnabled(data?.[data.emailId].enabled, isError(data?.errorCode)))}</p>
                                    </div>
                                    <div className='settings-sync-cal__actions-cntr'>
                                        <i className='icon-edit' onClick={() => handleEditSyncedCal(data)} /><span className="settings-sync-cal__edit" onClick={() => handleEditSyncedCal(data)}>{t('settingsScreen.edit')}</span>
                                        <i className="icon-trash" onClick={() => handleDeleteSyncedCal(data, false)} /><span className="settings-sync-cal__delete" onClick={() => handleDeleteSyncedCal(data, false)}>{t('settingsScreen.delete')}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                }
            </>
        )
    )
}